import { useEffect, useRef } from 'react';
import { GameEngine } from '../game/engine/GameEngine';
import { useGameStore } from '../store/gameStore';

/**
 * Thin React shell that owns the <canvas>, instantiates the engine on mount
 * and disposes it on unmount. The engine never re-renders React per frame —
 * HUD/leaderboard updates flow through Zustand at fixed cadences.
 *
 * The canvas hides the browser cursor — the engine paints a custom
 * crosshair in the player's team color at the cursor position.
 *
 * Player name is plumbed through here: we apply the initial value at mount
 * and subscribe to store changes so the engine's player snake (and therefore
 * the leaderboard) always reflects the latest name.
 */
export default function GameCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const setHud = useGameStore((s) => s.setHud);
  const setLeaderboard = useGameStore((s) => s.setLeaderboard);
  const setRespawn = useGameStore((s) => s.setRespawn);
  const setToggleMute = useGameStore((s) => s.setToggleMute);
  const setTogglePause = useGameStore((s) => s.setTogglePause);
  const setSkipRecap = useGameStore((s) => s.setSkipRecap);
  const setMobileControls = useGameStore((s) => s.setMobileControls);
  const setMobileSteerFn  = useGameStore((s) => s.setMobileSteerFn);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { chosenHue, chosenPattern, theme } = useGameStore.getState();
    const engine = new GameEngine(canvas, setHud, setLeaderboard, {
      playerHue: chosenHue,
      playerPattern: chosenPattern,
    });
    // Apply the initial theme + subscribe to changes so flipping it
    // mid-game updates the canvas immediately.
    engine.setLightMode(theme === 'light');

    // ── Disable the bottom-right minimap on mobile / touch devices.
    //    The operator asked for zero UI clutter on the small screen;
    //    the radar is desktop-only luxury. Re-checked on resize so a
    //    rotated tablet that crosses the threshold picks it up.
    const checkMobile = (): boolean => {
      const small = window.matchMedia('(max-width: 1024px)').matches;
      const touch = 'ontouchstart' in window ||
                    (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0);
      return small || touch;
    };
    engine.setMinimapEnabled(!checkMobile());
    const onResize = (): void => engine.setMinimapEnabled(!checkMobile());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    const unsubTheme = useGameStore.subscribe((state) => {
      engine.setLightMode(state.theme === 'light');
    });
    // 60s "session reward" wiring. The store flag survives navigation
    // (Home → Game → Home → Game) so if the user already earned it
    // earlier this browser session we pre-mark the engine as consumed
    // — otherwise the timer would fire again on every fresh game
    // canvas mount.
    const storeAtMount = useGameStore.getState();
    if (storeAtMount.rewardFiredThisSession) {
      engine.markSessionRewardConsumed();
    }
    engine.onSessionReward = () => useGameStore.getState().fireSessionReward();

    // Wake lock — request a screen wake lock so the phone doesn't
    // dim/sleep mid-game (the snake would die while the player tries
    // to wake the device). Re-requested on visibility change because
    // browsers release the lock when the tab is backgrounded.
    let wakeLock: { release: () => Promise<void> } | null = null;
    const requestWakeLock = async (): Promise<void> => {
      const wl = (navigator as unknown as { wakeLock?: { request: (kind: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock;
      if (!wl) return;
      try { wakeLock = await wl.request('screen'); } catch { /* deny */ }
    };
    void requestWakeLock();
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisibility);

    engine.start();
    // Respawn re-creates the player snake, which means a fresh Snake
    // instance with default `goldBorder = false`. Re-apply from the
    // store on every respawn so a player who earned the gold halo
    // keeps it through deaths within the same browser session.
    setRespawn(() => {
      engine.respawn();
      engine.setPlayerGoldBorder(useGameStore.getState().goldBorderActive);
    });
    setToggleMute(() => engine.toggleMute());
    setTogglePause(() => engine.togglePause());
    setSkipRecap(() => engine.skipRecap());
    setMobileControls((on: boolean) => engine.setBoost(on), () => engine.queueEject());
    setMobileSteerFn((angle) => engine.setMobileSteer(angle));

    // Apply initial player name and subscribe to changes. Same callback
    // also mirrors gold-border state onto the player snake so the
    // SnakeRenderer can draw the amber halo as soon as the 60s reward
    // fires (and on every respawn after, since the store flag is
    // session-scoped — the player keeps their crown).
    let lastName = useGameStore.getState().playerName;
    let lastGold = useGameStore.getState().goldBorderActive;
    if (lastName) engine.setPlayerName(lastName);
    engine.setPlayerGoldBorder(lastGold);
    const unsub = useGameStore.subscribe((state) => {
      if (state.playerName !== lastName) {
        lastName = state.playerName;
        engine.setPlayerName(lastName);
      }
      if (state.goldBorderActive !== lastGold) {
        lastGold = state.goldBorderActive;
        engine.setPlayerGoldBorder(lastGold);
      }
    });

    return () => {
      setRespawn(null);
      setToggleMute(null);
      setTogglePause(null);
      setSkipRecap(null);
      setMobileControls(null, null);
      setMobileSteerFn(null);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      void wakeLock?.release();
      unsub();
      unsubTheme();
      engine.dispose();
    };
  }, [setHud, setLeaderboard, setRespawn, setToggleMute, setTogglePause, setSkipRecap, setMobileControls, setMobileSteerFn]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full block"
      style={{ cursor: 'none' }}
      aria-label="CellSnake game viewport"
    />
  );
}
